<template>
  <div v-if="show" class="app-modal" role="presentation">
    <div class="app-modal__backdrop" @click="emitClose"></div>

    <section
      class="app-modal__dialog sale-status-modal__dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sale-status-modal-title"
      @click.stop
    >
      <header class="app-modal__header">
        <div>
          <h3 id="sale-status-modal-title">{{ text.title }}</h3>
          <p>{{ text.subtitle }}</p>
        </div>
        <button
          class="app-modal__close"
          type="button"
          :aria-label="text.closeAriaLabel"
          @click="emitClose"
        >
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </header>

      <div class="app-modal__content sale-status-modal__content">
        <form class="app-modal__form" @submit.prevent="emitSubmit">
          <section class="app-modal__section">
            <header class="app-modal__section-head">
              <h4>{{ normalizedSaleName }}</h4>
            </header>

            <div class="app-modal__grid two-columns">
              <div class="app-modal-field full-width">
                <span>{{ text.statusLabel }}</span>
                <multiselect
                  v-model="selectedStatus"
                  :options="normalizedStatuses"
                  track-by="value"
                  label="label"
                  :show-labels="false"
                  :searchable="true"
                  :allow-empty="false"
                  :max-height="114"
                  open-direction="down"
                  :placeholder="text.statusPlaceholder"
                  class="app-modal-select"
                >
                  <template #noResult>
                    <span>{{ commonText.noOption }}</span>
                  </template>
                  <template #noOptions>
                    <span>{{ commonText.noOption }}</span>
                  </template>
                </multiselect>
              </div>
            </div>

            <section class="sale-status-modal__help" aria-label="Guía de estados">
              <h5>{{ text.guideTitle }}</h5>
              <ul>
                <li><strong>{{ text.statuses.open }}:</strong> {{ text.guide.open }}</li>
                <li><strong>{{ text.statuses.closed }}:</strong> {{ text.guide.closed }}</li>
                <li><strong>{{ text.statuses.canceled }}:</strong> {{ text.guide.canceled }}</li>
              </ul>
            </section>
          </section>

          <footer class="app-modal__footer">
            <div class="app-modal__footer-right">
              <button type="button" class="btn btn-light" :disabled="submitting" @click="emitClose">
                {{ text.actions.cancel }}
              </button>
              <button type="submit" class="btn btn-primary" :disabled="submitting || !selectedStatusId">
                {{ submitting ? text.actions.submitting : text.actions.submit }}
              </button>
            </div>
          </footer>
        </form>
      </div>
    </section>
  </div>
</template>

<script>
import { SALES_TEXT } from '@/texts/catalog/sales';

const SHARED_TEXT = SALES_TEXT.shared;
const STATUS_MODAL_TEXT = SHARED_TEXT.statusModal;

export default {
  name: 'SaleStatusModal',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    saleName: {
      type: String,
      default: '',
    },
    currentStatusLabel: {
      type: String,
      default: '',
    },
    statuses: {
      type: Array,
      default: () => [],
    },
    selectedStatusId: {
      type: Number,
      default: 0,
    },
    submitting: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'submit'],
  data() {
    return {
      text: STATUS_MODAL_TEXT,
      commonText: SHARED_TEXT.common,
      selectedStatus: null,
    };
  },
  computed: {
    normalizedSaleName() {
      const value = String(this.saleName || '').trim();
      return value !== '' ? value : this.commonText.emptySaleName;
    },
    normalizedStatuses() {
      return (Array.isArray(this.statuses) ? this.statuses : [])
        .filter((status) => Number(status?.value || 0) > 0);
    },
  },
  watch: {
    show(visible) {
      if (visible) {
        this.syncSelectedStatus();
        return;
      }

      this.selectedStatus = null;
    },
    selectedStatusId() {
      if (!this.show) {
        return;
      }

      this.syncSelectedStatus();
    },
    statuses() {
      if (!this.show) {
        return;
      }

      this.syncSelectedStatus();
    },
  },
  methods: {
    emitClose() {
      if (this.submitting) {
        return;
      }

      this.$emit('close');
    },
    syncSelectedStatus() {
      const targetId = Number(this.selectedStatusId || 0);
      if (targetId <= 0) {
        this.selectedStatus = null;
        return;
      }

      this.selectedStatus = this.normalizedStatuses
        .find((status) => Number(status?.value || 0) === targetId) || null;
    },
    emitSubmit() {
      const statusId = Number(this.selectedStatus?.value || 0);
      if (statusId <= 0 || this.submitting) {
        return;
      }

      this.$emit('submit', statusId);
    },
  },
};
</script>

<style scoped>
.sale-status-modal__dialog {
  max-width: 540px;
  height: auto;
  max-height: calc(100vh - 48px);
}

.sale-status-modal__content {
  min-height: auto;
  overflow: visible;
}

.sale-status-modal__content .app-modal__section {
  padding-bottom: 18px;
}

.app-modal-select {
  width: 100%;
}

.sale-status-modal__help {
  margin-top: 12px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 8px;
  background: rgba(241, 245, 249, 0.55);
  padding: 10px 12px;
}

.sale-status-modal__help h5 {
  margin: 0 0 6px;
  font-size: 12px;
  color: #334155;
  font-weight: 700;
}

.sale-status-modal__help ul {
  margin: 0;
  padding-left: 16px;
}

.sale-status-modal__help li {
  font-size: 12px;
  line-height: 1.4;
  color: #475569;
  margin-bottom: 4px;
}

.sale-status-modal__help li:last-child {
  margin-bottom: 0;
}
</style>